// ---------------------------------------------------------------------------
// AddTestSetDataDialog — Select data entries from a parent dataset to add
// to a test set. Shows version selector, searchable entry list with
// checkboxes, and "Add N Entries" action button.
// ---------------------------------------------------------------------------

import {
  Body1,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Select,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from '@fluentui/react-components';
import { Search20Regular } from '@fluentui/react-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDataHubClient } from '../../../context/DataHubClientContext.js';
import type {
  DataEntry,
  DatasetVersion,
} from '../../../services/dataHubService.interface.js';
import { DataHubService } from '../../../services/dataHubService.js';
import { TestSetService } from '../../../services/testSetService.js';
import { withCorrelationId } from '../../../services/usageContext.js';
import { useScenarioFactory } from '../../../telemetry/hooks/useScenarioFactory/useScenarioFactory.js';
import { classifyScenarioError } from '../../../telemetry/scenarioError.js';
import { ScenarioName } from '../../../telemetry/scenarios.js';

import type { AddTestSetDataDialogProps } from './AddTestSetDataDialog.interface.js';
import { useStyles } from './AddTestSetDataDialog.style.js';

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function AddTestSetDataDialog({
  open,
  testSetId,
  dataSetId,
  onClose,
  onAdded,
}: AddTestSetDataDialogProps) {
  const styles = useStyles();
  const client = useDataHubClient();
  const scenarioFactory = useScenarioFactory();
  const dataHubService = useMemo(() => new DataHubService(client), [client]);
  const testSetService = useMemo(() => new TestSetService(client), [client]);

  const [versions, setVersions] = useState<DatasetVersion[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [entries, setEntries] = useState<DataEntry[]>([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [isLoadingEntries, setIsLoadingEntries] = useState(false);
  const [entriesError, setEntriesError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchFilter, setSearchFilter] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Load dataset versions
  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setIsLoadingVersions(true);

    dataHubService
      .listDatasetVersions({
        dataSetId,
        pageSize: 100,
        includeCount: false,
      })
      .then((result) => {
        if (cancelled) return;
        setVersions(result.items);
        // Default to the latest published version
        const published = result.items.find((v) => v.status === 'Published');
        const latest = published ?? result.items[0];
        if (latest) {
          setSelectedVersion(latest.version);
        }
        setIsLoadingVersions(false);
      })
      .catch(() => {
        if (cancelled) return;
        setIsLoadingVersions(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, dataHubService, dataSetId]);

  // Load entries for selected version
  useEffect(() => {
    if (!open || selectedVersion === null) return;

    let cancelled = false;
    setIsLoadingEntries(true);
    setEntriesError(null);

    dataHubService
      .listDataEntries({
        dataSetId,
        dataSetVersion: selectedVersion,
        pageSize: 100,
        includeCount: true,
      })
      .then((result) => {
        if (cancelled) return;
        setEntries(result.items);
        setIsLoadingEntries(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setEntriesError(
          err instanceof Error ? err.message : 'Failed to load entries',
        );
        setIsLoadingEntries(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, dataHubService, dataSetId, selectedVersion]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedIds(new Set());
      setSearchFilter('');
      setSubmitError(null);
    }
  }, [open]);

  const filteredEntries = useMemo(() => {
    if (searchFilter.trim() === '') return entries;
    const q = searchFilter.toLowerCase();
    return entries.filter(
      (e) =>
        (e.name ?? '').toLowerCase().includes(q) ||
        e.dataId.toLowerCase().includes(q),
    );
  }, [entries, searchFilter]);

  const handleToggle = useCallback((dataId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(dataId)) {
        next.delete(dataId);
      } else {
        next.add(dataId);
      }
      return next;
    });
  }, []);

  const handleToggleAll = useCallback(() => {
    if (selectedIds.size === filteredEntries.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredEntries.map((e) => e.dataId)));
    }
  }, [selectedIds.size, filteredEntries]);

  const handleSubmit = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setIsSubmitting(true);
    setSubmitError(null);

    const scenario = scenarioFactory.newScenario(ScenarioName.TestsetAddData);
    try {
      await testSetService.addTestSetData(testSetId, [...selectedIds], {
        extras: withCorrelationId(scenario.queryId),
      });
      scenario.complete('success');
      onAdded();
      onClose();
    } catch (err) {
      const classification = classifyScenarioError(err);
      scenario.complete(classification.status, classification.data);
      setSubmitError(
        err instanceof Error ? err.message : 'Failed to add entries',
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    testSetService,
    testSetId,
    selectedIds,
    onAdded,
    onClose,
    scenarioFactory,
  ]);

  const allChecked =
    filteredEntries.length > 0 &&
    filteredEntries.every((e) => selectedIds.has(e.dataId));

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) onClose();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Select Data from Dataset</DialogTitle>
          <DialogContent>
            <div className={styles.header}>
              <span className={styles.datasetName}>Dataset: {dataSetId}</span>
            </div>

            {/* Version selector */}
            <div className={styles.versionRow}>
              <span className={styles.versionLabel}>Version</span>
              {isLoadingVersions ? (
                <Spinner size="tiny" />
              ) : (
                <Select
                  size="small"
                  value={
                    selectedVersion !== null ? String(selectedVersion) : ''
                  }
                  onChange={(_, data) => setSelectedVersion(Number(data.value))}
                  aria-label="Dataset version"
                >
                  {versions.map((v) => (
                    <option key={v.version} value={v.version}>
                      v{v.version} ({v.status.toLowerCase()})
                    </option>
                  ))}
                </Select>
              )}
            </div>

            {/* Search */}
            <Input
              className={styles.searchInput}
              contentBefore={<Search20Regular />}
              placeholder="Search entries by name..."
              value={searchFilter}
              onChange={(_, data) => setSearchFilter(data.value)}
              aria-label="Search entries"
            />

            {/* Entry list with checkboxes */}
            <div className={styles.entryList}>
              {isLoadingEntries ? (
                <div className={styles.loadingState}>
                  <Spinner size="small" label="Loading entries..." />
                </div>
              ) : entriesError !== null ? (
                <div className={styles.errorState}>{entriesError}</div>
              ) : (
                <Table className={styles.table}>
                  <TableHeader className={styles.tableHeader}>
                    <TableRow>
                      <TableHeaderCell className={styles.checkboxCell}>
                        <Checkbox
                          checked={allChecked}
                          onChange={handleToggleAll}
                          aria-label="Select all"
                        />
                      </TableHeaderCell>
                      <TableHeaderCell>Name</TableHeaderCell>
                      <TableHeaderCell>Type</TableHeaderCell>
                      <TableHeaderCell>Created</TableHeaderCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredEntries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell className={styles.checkboxCell}>
                          <Checkbox
                            checked={selectedIds.has(entry.dataId)}
                            onChange={() => handleToggle(entry.dataId)}
                            aria-label={`Select ${entry.name ?? entry.dataId}`}
                          />
                        </TableCell>
                        <TableCell>{entry.name ?? entry.dataId}</TableCell>
                        <TableCell>{entry.dataType ?? '—'}</TableCell>
                        <TableCell>{formatDate(entry.createdAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {!isLoadingEntries && entriesError === null && (
                <div className={styles.endOfList}>End of list</div>
              )}
            </div>

            <div className={styles.selectionCount}>
              {selectedIds.size} entries selected
            </div>

            {submitError !== null && (
              <Body1 className={styles.submitError}>{submitError}</Body1>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              appearance="primary"
              disabled={selectedIds.size === 0 || isSubmitting}
              icon={isSubmitting ? <Spinner size="tiny" /> : undefined}
              onClick={() => {
                void handleSubmit();
              }}
            >
              Add {selectedIds.size} Entries
            </Button>
            <Button
              appearance="secondary"
              disabled={isSubmitting}
              onClick={onClose}
            >
              Cancel
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
